import React, { PropsWithChildren, Suspense, useEffect, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../icon';
import { useAgentAssistant } from '../agentAssistant/AgentAssistantContext';
import { SkeletonBlock, SkeletonStack } from '../skeleton';
import { ZTooltip } from '../ztooltip';

import { useRelayStore } from 'src/relay';
import { useAppState } from 'src/shared/appState';

import { type EnvType, useLastUsedTool } from '../../../shared/hooks';

import { ZakekeEnvironment } from 'src/shared/enums';
import { getPathName, T } from 'src/shared/helpers';
import MenuOpenIcon from 'src/shared/material_icons/navigation/menu_open.svg';
import SmartToyIcon from 'src/shared/material_icons/hardware/smart_toy.svg';
import { usePageTransition } from 'src/shared/navigationTransition';
import LogoZakeke from 'src/shared/svg/logoZakeke';
import { GetIcon, GetMenuItemIcon } from './IconManager';
import { ItemMenu } from './Menu';
import SidebarFooter from './SidebarFooter';
import SidebarMenuItems from './SidebarMenuItems';
import SidebarOnboardingPanel from './SidebarOnboardingPanel';
import SidebarSearchHelp from './SidebarSearchHelp';
import SmallMenu from './SmallMenu';
import SolutionSwitcher from './SolutionSwitcher';
import { AppLeftSidebarMainQuery } from './__generated__/AppLeftSidebarMainQuery.graphql';
import type { LeftAppSidebarState } from './leftAppSidebarTypes';

import { isMenuItemKeySelected, isMenuItemSelected } from './helpers';
import {
    Body,
    CollapsedAiModeButton,
    CollapsedNavigation,
    CollapsedNavigationLink,
    ContainerLeft,
    ContainerLeftBottom,
    ContainerLeftTop,
    ContainerRight,
    Header,
    HeaderActions,
    HeaderAiModeButton,
    HeaderCollapseButton,
    HeaderLogoLink,
    Main,
    SidebarBottomUtilities,
    SidebarContent,
    SidebarTopSearch
} from './leftAppSidebarStyles';

export interface AppLeftSidebarProps {
    isMenuHidden: boolean;
    isCollapsed: boolean;
    onCollapsedChange: (isCollapsed: boolean) => void;
    initialState?: LeftAppSidebarState | null;
    onStateChange?: (state: LeftAppSidebarState) => void;
}

function getSelectedSubMenu(root: ItemMenu): ItemMenu {
    if (window.location.href.toLowerCase().includes('dam')) {
        const findDamMenu = (item: ItemMenu): ItemMenu | null => {
            if (item.name.toLowerCase() === 'dam') return item;

            for (const child of item.menu) {
                const damMenu = findDamMenu(child);
                if (damMenu) return damMenu;
            }

            return null;
        };

        const damMenu = findDamMenu(root);
        if (damMenu) return damMenu;
    }

    let selected = root;
    const findSelected = (item: ItemMenu) => {
        if (item.menu.length > 0) {
            for (const x of item.menu) {
                // Skip "home" because it's the root
                if (x.name.toLowerCase() === 'home') continue;

                const isSelected = isMenuItemSelected(x);
                if (isSelected) {
                    // Don't drill into forceAsNotSubmenu parents (Tools, Settings):
                    // their children are rendered inline as categories at HOME.
                    if (!item.forceAsNotSubmenu) {
                        selected = item;
                    }
                    return;
                }

                findSelected(x);
            }
        }
    };

    findSelected(root);

    return selected;
}

const findMenuBySavedState = (root: ItemMenu, savedState?: LeftAppSidebarState | null): ItemMenu | null => {
    if (!savedState?.currentMenuKey && !savedState?.currentMenuUrlPage) return null;

    const walk = (item: ItemMenu): ItemMenu | null => {
        if (
            (savedState.currentMenuKey && item.key === savedState.currentMenuKey) ||
            (savedState.currentMenuUrlPage && item.urlPage === savedState.currentMenuUrlPage)
        ) {
            return item;
        }

        for (const child of item.menu) {
            const match = walk(child);
            if (match) return match;
        }

        return null;
    };

    return walk(root);
};

const normalizeMenuName = (name: string) =>
    name
        .trim()
        .toLowerCase()
        .replace(/[-_\s]+/g, ' ');

const isHomeMenuItem = (item: ItemMenu) => normalizeMenuName(item.name) === 'home';

const findMenuItemByKey = (item: ItemMenu, key: string): ItemMenu | null => {
    if (item.key === key) return item;

    for (const child of item.menu) {
        const match = findMenuItemByKey(child, key);
        if (match) return match;
    }

    return null;
};

const isQuickAccessMenuItem = (item: ItemMenu) => normalizeMenuName(item.name) === 'quick access';

const isHiddenCollapsedMenuItem = (item: ItemMenu) => {
    const normalizedName = normalizeMenuName(item.name);
    const normalizedUrl = item.urlPage.toLowerCase();

    return normalizedName === 'dam' || normalizedName === 'home' || normalizedName === 'dashboard' || normalizedUrl.includes('/dashboard');
};

const hasCollapsedNavigationUrl = (item: ItemMenu) => {
    const url = item.urlPage?.trim();
    return Boolean(url && !/^\d+$/.test(url));
};

const getCollapsedCategoryKey = (item: ItemMenu) => item.key || item.urlPage || normalizeMenuName(item.name);

const isMenuTreeSelected = (item: ItemMenu): boolean =>
    isMenuItemSelected(item) || isMenuItemKeySelected(item) || item.menu.some((child) => isMenuTreeSelected(child));

const getCollapsedNavigationItems = (rootMenu: ItemMenu) => {
    const items: ItemMenu[] = [];
    const keys = new Set<string>();

    const appendItem = (item: ItemMenu) => {
        if (isHiddenCollapsedMenuItem(item) || isQuickAccessMenuItem(item)) return;

        const key = item.key || item.urlPage || item.name;
        if (keys.has(key)) return;

        keys.add(key);
        items.push(item);
    };

    const homeMenu = rootMenu.menu.find(isHomeMenuItem);
    homeMenu?.menu.forEach(appendItem);

    rootMenu.menu.filter((item) => !isHomeMenuItem(item)).forEach(appendItem);

    return items;
};
const sidebarQuery = graphql`
    query AppLeftSidebarMainQuery($lastUsedTool: Int!) {
        treeMenu(lastUsedTool: $lastUsedTool)
        currentIAMUser {
            isIAMUser
            permissions
        }
    }
`;

// NOTE: SidebarInner is a module-level component (stable reference) so React
// keeps its useState instances across AppLeftSidebar re-renders. Remounting
// only happens on `lastUsedTool` change via the `key` prop below.
interface SidebarInnerProps {
    lastUsedTool: number;
    isMenuHidden: boolean;
    isCollapsed: boolean;
    onCollapsedChange: (isCollapsed: boolean) => void;
    setLastUsedTool: (tool: EnvType) => Promise<void>;
    initialState?: LeftAppSidebarState | null;
    onStateChange?: (state: LeftAppSidebarState) => void;
}

function SidebarInner({ lastUsedTool, isMenuHidden, isCollapsed, onCollapsedChange, setLastUsedTool, initialState, onStateChange }: SidebarInnerProps) {
    const navigate = useNavigate();
    const { startPageTransition } = usePageTransition();
    const [isDirty, incrementStoreKey] = useRelayStore((state) => [state.isRelayStoreDirty, state.incrementRelayStoreKey]);
    const data = useLazyLoadQuery<AppLeftSidebarMainQuery>(sidebarQuery, { lastUsedTool }, { fetchPolicy: 'store-and-network' });
    const rootMenu = JSON.parse(data.treeMenu) as ItemMenu;

    const [currentMenu, setCurrentMenu] = useState(() => findMenuBySavedState(rootMenu, initialState) ?? getSelectedSubMenu(rootMenu));
    const [expandedCategoryKey, setExpandedCategoryKey] = useState<string | null>(null);
    const [openCategoriesSnapshot, setOpenCategoriesSnapshot] = useState<Record<string, boolean> | undefined>(initialState?.openCategories);
    const { isEnabled: isAgentEnabled, openAiMode } = useAgentAssistant();
    const dashboardItem = findMenuItemByKey(rootMenu, 'dashboard');
    const dashboardUrl = dashboardItem?.urlPage ?? '';

    useEffect(() => {
        onStateChange?.({
            currentMenuKey: currentMenu.key,
            currentMenuUrlPage: currentMenu.urlPage,
            openCategories: openCategoriesSnapshot
        });
    }, [currentMenu, onStateChange, openCategoriesSnapshot]);

    const handleDashboardClick = async (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        const isDam = window.location.href.toLowerCase().includes('dam');

        const navigationEvent = new CustomEvent('app-navigate-request', {
            detail: {
                path: dashboardUrl,
                urlPage: dashboardUrl,
                isDam
            },
            cancelable: true
        });

        window.dispatchEvent(navigationEvent);
        if (navigationEvent.defaultPrevented) return;

        if (isDirty) incrementStoreKey();

        if (isDam || lastUsedTool !== ZakekeEnvironment.Customizer) {
            try {
                await setLastUsedTool('CUSTOMIZER');
            } catch {
                // The hook logs mutation failures; still perform the requested full refresh.
            } finally {
                window.location.href = dashboardUrl;
            }
            return;
        } else {
            startPageTransition(() => {
                navigate(dashboardUrl);
            });
        }
    };

    const handleCollapsedMenuItemClick = (item: ItemMenu) => (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();

        if (!hasCollapsedNavigationUrl(item)) {
            if (item.menu.length > 0) {
                setCurrentMenu(rootMenu);
                setExpandedCategoryKey(getCollapsedCategoryKey(item));
            }

            onCollapsedChange(false);
            return;
        }

        setExpandedCategoryKey(null);

        const navigationEvent = new CustomEvent('app-navigate-request', {
            detail: {
                path: getPathName(item.urlPage),
                urlPage: item.urlPage,
                isDam: window.location.href.toLowerCase().includes('dam')
            },
            cancelable: true
        });

        window.dispatchEvent(navigationEvent);
        if (navigationEvent.defaultPrevented) return;

        if (isDirty) incrementStoreKey();

        const pathname = getPathName(item.urlPage);
        if (window.location.href.toLowerCase().includes('dam')) window.location.href = pathname;
        else {
            startPageTransition(() => {
                navigate(pathname);
            });
        }
    };

    const iamUser = data.currentIAMUser.isIAMUser;
    const iamUserPermissions = data.currentIAMUser.permissions as string[];
    const collapseLabel = isCollapsed ? T._('Expand menu', 'Admin') : T._('Collapse menu', 'Admin');
    const aiModeLabel = T._('Enter AI mode', 'Admin');
    const collapsedNavigationItems = getCollapsedNavigationItems(rootMenu);

    if (isMenuHidden) return null;

    const renderHeader = () => (
        <Header $isCollapsed={isCollapsed}>
            {!isCollapsed &&
                (dashboardItem ? (
                    <HeaderLogoLink href={dashboardUrl} onClick={handleDashboardClick}>
                        <LogoZakeke />
                    </HeaderLogoLink>
                ) : (
                    <LogoZakeke />
                ))}
            <HeaderActions>
                {!isCollapsed && isAgentEnabled && (
                    <ZTooltip position='bottom' title={aiModeLabel}>
                        <HeaderAiModeButton type='button' aria-label={aiModeLabel} onClick={openAiMode}>
                            <Icon aria-hidden='true'>
                                <SmartToyIcon />
                            </Icon>
                        </HeaderAiModeButton>
                    </ZTooltip>
                )}
                <ZTooltip position='bottom' title={collapseLabel}>
                    <HeaderCollapseButton
                        type='button'
                        aria-label={collapseLabel}
                        $isCollapsed={isCollapsed}
                        onClick={() => {
                            setExpandedCategoryKey(null);
                            onCollapsedChange(!isCollapsed);
                        }}
                    >
                        <Icon aria-hidden='true'>
                            <MenuOpenIcon />
                        </Icon>
                    </HeaderCollapseButton>
                </ZTooltip>
            </HeaderActions>
        </Header>
    );

    const renderCollapsedNavigation = () => {
        const DashboardIcon = dashboardItem ? GetMenuItemIcon(dashboardItem) : undefined;
        const dashboardLabel = dashboardItem?.name ?? '';
        const isDashboardSelected = dashboardItem ? isMenuTreeSelected(dashboardItem) : false;

        return (
            <CollapsedNavigation aria-label={T._('Main navigation', 'Admin')}>
                {DashboardIcon && dashboardItem && (
                    <ZTooltip position='right' title={dashboardLabel}>
                        <CollapsedNavigationLink
                            href={dashboardUrl}
                            aria-label={dashboardLabel}
                            $isSelected={isDashboardSelected}
                            onClick={handleDashboardClick}
                        >
                            <DashboardIcon />
                        </CollapsedNavigationLink>
                    </ZTooltip>
                )}

                {isAgentEnabled && (
                    <ZTooltip position='right' title={aiModeLabel}>
                        <CollapsedAiModeButton type='button' aria-label={aiModeLabel} onClick={openAiMode}>
                            <Icon aria-hidden='true'>
                                <SmartToyIcon />
                            </Icon>
                        </CollapsedAiModeButton>
                    </ZTooltip>
                )}

                {collapsedNavigationItems.map((item) => {
                    const ItemIcon = GetIcon(item.icon);
                    if (!ItemIcon) return null;

                    return (
                        <ZTooltip key={item.key || item.urlPage || item.name} position='right' title={item.name}>
                            <CollapsedNavigationLink
                                href={hasCollapsedNavigationUrl(item) ? item.urlPage : 'javascript:void(0)'}
                                aria-label={item.name}
                                $isSelected={isMenuTreeSelected(item)}
                                onClick={handleCollapsedMenuItemClick(item)}
                            >
                                <ItemIcon />
                            </CollapsedNavigationLink>
                        </ZTooltip>
                    );
                })}
            </CollapsedNavigation>
        );
    };

    const renderDefaultMenuContent = () => (
        <SidebarMenuItems
            homeMenu={rootMenu}
            currentMenu={currentMenu}
            goToMenu={setCurrentMenu}
            onBackClick={() => {
                if (window.location.href.includes('DAM')) {
                    setLastUsedTool('CUSTOMIZER'); // forzo il cambio tool AD 1
                    window.location.href = 'Dashboard';
                    return;
                }
                setCurrentMenu(rootMenu);
            }}
            iamUser={iamUser}
            iamUserPermissions={iamUserPermissions}
            expandedCategoryKey={expandedCategoryKey}
            onExpandedCategoryOpened={() => setExpandedCategoryKey(null)}
            initialOpenCategories={initialState?.openCategories}
            onOpenCategoriesChange={setOpenCategoriesSnapshot}
        />
    );

    return (
        <Main id='main-left-sidebar' $isCollapsed={isCollapsed}>
            <SidebarContent $isCollapsed={isCollapsed}>
                {renderHeader()}

                {isCollapsed ? (
                    <>
                        {renderCollapsedNavigation()}
                        <SidebarFooter isCollapsed />
                    </>
                ) : (
                    <Body>
                        <ContainerLeft>
                            <ContainerLeftTop>
                                <SolutionSwitcher />
                                <SidebarTopSearch>
                                    <SidebarSearchHelp />
                                </SidebarTopSearch>
                                {renderDefaultMenuContent()}
                            </ContainerLeftTop>

                            <ContainerLeftBottom>
                                <SidebarBottomUtilities>
                                    <Suspense fallback={null}>
                                        <SidebarOnboardingPanel />
                                    </Suspense>
                                </SidebarBottomUtilities>
                                <SidebarFooter />
                            </ContainerLeftBottom>
                        </ContainerLeft>

                        <ContainerRight $numOfShadow={1}>
                            <SmallMenu
                                homeMenu={rootMenu}
                                currentMenu={currentMenu}
                                goToMenu={setCurrentMenu}
                                onBackClick={() => setCurrentMenu(rootMenu)}
                                iamUser={iamUser}
                                iamUserPermissions={iamUserPermissions}
                            />
                        </ContainerRight>
                    </Body>
                )}
            </SidebarContent>
        </Main>
    );
}

export function AppLeftSidebar(props: AppLeftSidebarProps) {
    const { state } = useAppState();
    const { setLastUsedTool } = useLastUsedTool();
    const lastUsedTool = state.lastUsedTool;

    return (
        <SidebarInner
            key={lastUsedTool}
            lastUsedTool={lastUsedTool}
            isMenuHidden={props.isMenuHidden}
            isCollapsed={props.isCollapsed}
            onCollapsedChange={props.onCollapsedChange}
            setLastUsedTool={setLastUsedTool}
            initialState={props.initialState}
            onStateChange={props.onStateChange}
        />
    );
}

const LeftAppSidebarSkeleton = () => {
    return (
        <Main>
            <SidebarContent>
                <Body>
                    <ContainerLeft>
                        <SkeletonStack aria-hidden='true' $gap='13px'>
                            {Array.from({ length: 6 }).map((_, index) => (
                                <SkeletonBlock key={index} $width='90%' $height='27px' $radius='10px' $margin={index === 0 ? '20px 0 0 10px' : '0 0 0 10px'} />
                            ))}
                        </SkeletonStack>
                    </ContainerLeft>
                </Body>
            </SidebarContent>
        </Main>
    );
};

interface LeftAppSidebarProps {
    isMenuHidden: boolean;
    isCollapsed: boolean;
    onCollapsedChange: (isCollapsed: boolean) => void;
    initialState?: LeftAppSidebarState | null;
    onStateChange?: (state: LeftAppSidebarState) => void;
}

const LeftAppSidebar = (props: PropsWithChildren<LeftAppSidebarProps>) => {
    return (
        <Suspense fallback={!props.isMenuHidden ? <LeftAppSidebarSkeleton /> : null}>
            <AppLeftSidebar
                isMenuHidden={props.isMenuHidden}
                isCollapsed={props.isCollapsed}
                onCollapsedChange={props.onCollapsedChange}
                initialState={props.initialState}
                onStateChange={props.onStateChange}
            />
        </Suspense>
    );
};

export default LeftAppSidebar;
